/**
 * The task-screen door's chain step (spec 2026-09-20 §3.2; plan R-P2; review T7
 * rounds 1 and 2, R-L9): the pure part of what useComposerConverse does with a
 * verdict, so it pins without a hook. The hook keeps its refs and UI writes and
 * lends them here as a DoorHost.
 */
import type {
  FileAttachment,
  ImageAttachment,
  TaskDoorControl,
  TaskDoorRoute,
} from "@bash/shared";
import { TASK_DOOR_MAX_CHARS } from "@bash/shared";
import { applyPrefill } from "@/components/message-input/prefill";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { useTaskStore } from "@/stores/taskStore";

/** A send on the chain: the words as typed, the task they were typed on, and what rode along. */
export interface DoorSend {
  taskId: string;
  text: string;
  images: ImageAttachment[] | undefined;
  files: FileAttachment[] | undefined;
  /** The request id of the send this one cancelled: named if the door hands it to the composer. */
  replaces?: string;
}

/** What the hook lends a step: its args at step time, whether it is still mounted, and the UI moves it owns. */
export interface DoorHost {
  classicSend: (
    text: string,
    images?: ImageAttachment[],
    files?: FileAttachment[]
  ) => void;
  /** Runs a control and says whether it handled it (R-L22): a stop with nothing in flight is not handled, and the words ride the task as a message. */
  onControl?: (control: TaskDoorControl, taskId: string) => boolean;
  /** Still mounted: a card may be painted and an inline answer started. */
  live: boolean;
  /** Paint the control receipt with the server's words, superseding an inline answer still streaming. */
  showReceipt: (words: string) => void;
  /** Start the inline answer for this send. */
  startAnswer: () => void;
  /** After a throw: clear the receipt, idle the card. */
  idle: () => void;
}

/** The verdict a send the door never asks about carries (R-P1 `not_asked`): the words ride the task. */
export const NOT_ROUTED: TaskDoorRoute = { to: "task", why: "not_asked" };

/** A plain sentence: words alone, not a teammate address (`@handle …`), not a slash command, and not over the server's cap (R-L23: a longer one is an instruction, and the route would only answer not_asked); those ride the task as today (spec 2026-09-20 §3.2). The person's own words, so a regex is the right tool here. */
export const isPlainSentence = (
  trimmed: string,
  hasAttachments: boolean
): boolean =>
  Boolean(trimmed) &&
  trimmed.length <= TASK_DOOR_MAX_CHARS &&
  !hasAttachments &&
  !/^[@/]/.test(trimmed);

/**
 * The verdict landed after the person picked another task (review T7 #3): the
 * classic send reads the selected task at call time and would land the words on
 * the wrong one, so they go back to the box that owns them instead, as a refused
 * send's do (sendMessage's handBackRefusedWords). A second hand-back joins the
 * first the way the box joins an append (round 2), never overwrites it, and a
 * prior draft keeps its mode. True when handed back.
 *
 * A send carrying a photo or a file is never handed back (review T7 R1): the box
 * holds words only, so its attachment would vanish without a trace. It takes the
 * classic rail to the task now selected instead, where the person can see it.
 */
export function handBackIfTaskChanged(
  taskId: string,
  text: string,
  hasAttachments = false
): boolean {
  if (useTaskStore.getState().selectedTaskId === taskId) return false;
  if (hasAttachments) return false;
  if (text.trim()) {
    const draft = useComposerDraftStore.getState();
    const prior = draft.prefillByTask[taskId];
    const merged = applyPrefill(prior?.text ?? "", text, "append");
    if (prior?.mode === "replace") draft.requestPrefill(taskId, merged);
    else draft.appendPrefill(taskId, merged);
  }
  return true;
}

/** The classic rail from the chain (review T7 #1, R-L3): nothing is left to fall to, so a throw is logged and never left to stick the chain. */
export function sendClassicOnChain(
  host: Pick<DoorHost, "classicSend">,
  text: string,
  images: ImageAttachment[] | undefined,
  files: FileAttachment[] | undefined
): void {
  try {
    host.classicSend(text, images, files);
  } catch (err) {
    console.error("[composer door] classic send threw", err);
  }
}

/**
 * One step of the chain: the task guard, then the verdict. A step never throws
 * (R-L3, R-L21): a store subscriber, a host move or a handler that throws idles
 * the card and falls to the classic rail, so the words are never lost. On a
 * host that is no longer live nothing is painted and no inline answer starts;
 * a control the host did not handle (R-L22) paints nothing either. In both the
 * words ride the task.
 */
export function runDoorStep(
  v: TaskDoorRoute,
  send: DoorSend,
  host: DoorHost
): void {
  const hasAttachments = Boolean(send.images?.length || send.files?.length);
  try {
    if (handBackIfTaskChanged(send.taskId, send.text, hasAttachments)) return;
    if (host.live && v.to === "control" && host.onControl) {
      if (host.onControl(v.control, send.taskId)) {
        host.showReceipt(v.words);
        return;
      }
    }
    if (host.live && v.to === "composer") {
      host.startAnswer();
      return;
    }
  } catch {
    host.idle();
  }
  sendClassicOnChain(host, send.text, send.images, send.files);
}

/**
 * The chain's belt (R-L21): a step is appended so that a rejection (a store
 * subscriber throwing inside a set, say) is logged and the chain still settles;
 * a rejected chain would skip every later step and drop every later send.
 */
export function chainDoorStep(
  prev: Promise<void>,
  step: () => Promise<void>
): Promise<void> {
  return prev.then(step).catch((err: unknown) => {
    console.error("[composer door] step rejected", err);
  });
}
